import { ChevronDownIcon, ChevronUpIcon } from '@calizahq/react-components-v2'
import clsx from 'clsx'
import { formatInTimeZone } from 'date-fns-tz'
import React, { useEffect, useRef, useState } from 'react'

import { TicketComment } from '@/types/TicketComment'

import { Comment } from './Comment'

type Props = {
  activeCommentToReply: number | undefined
  data: TicketComment
  isReadOnly?: boolean
  scrollToElement: number | undefined
  setActiveCommentToReply: (commentId?: number) => void
  supportedReactions?: string[] | null
}

export const ParentComment = ({
  data,
  activeCommentToReply,
  isReadOnly,
  scrollToElement,
  setActiveCommentToReply,
  supportedReactions,
}: Props) => {
  const [showChildComments, setShowChildComments] = useState(false)
  const [scrolledCurrentElement, setScrolledCurrentElement] = useState(false)
  const commentRef = useRef<HTMLDivElement>(null)
  const { creator, childComments, externalCreatedByRole } = data
  const externalUsers = [0, 1, 19]
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const lastUpdate =
    childComments && childComments.length > 0 ? childComments[childComments.length - 1].createdAt : undefined

  const lastUpdateTime = lastUpdate ? formatInTimeZone(lastUpdate, timezone, 'MMM d yyyy, HH:mm a z') : undefined
  const isScrollToCurrentElement = scrollToElement === data.id

  // Priority: Use externalCreatedByRole if it exists, otherwise fall back to creator.role
  const isHost = externalCreatedByRole === 'HOST' || externalCreatedByRole === 'COHOST'
  const isInternalUser = !externalCreatedByRole && !externalUsers.includes(creator.role)

  useEffect(() => {
    if (commentRef.current && isScrollToCurrentElement && !scrolledCurrentElement) {
      commentRef.current.scrollIntoView({ behavior: 'smooth' })
      setScrolledCurrentElement(true)
    } else if (scrolledCurrentElement && !isScrollToCurrentElement) {
      setScrolledCurrentElement(false)
    }
    return () => {}
  }, [isScrollToCurrentElement, scrollToElement, scrolledCurrentElement])

  const toggleChildComments = () => {
    setShowChildComments((prev) => {
      return !prev
    })
  }

  // Right-align if: internal MC user OR host
  // For Airbnb messages, alignment is based on role (HOST vs GUEST), not internalOnly flag
  // internalOnly is about message visibility, not who sent it
  const rightAllignment = isInternalUser || isHost
  return (
    <div
      className={clsx(
        'flex w-5/6 flex-col px-3 md:w-2/3',
        rightAllignment && 'self-end',
        !!childComments ? 'mb-4' : 'mb-0 md:mb-4',
      )}
      id={data.id + ''}
      ref={commentRef}
    >
      <Comment
        activeCommentToReply={activeCommentToReply}
        data={data}
        isReadOnly={isReadOnly}
        rightAllignment={rightAllignment}
        scrolledComment={scrolledCurrentElement}
        setActiveCommentToReply={setActiveCommentToReply}
        supportedReactions={supportedReactions}
      />
      {childComments && (
        <div className="flex  w-full flex-col">
          <div
            className={`flex justify-end px-2 md:items-center md:justify-normal ${
              rightAllignment ? '' : ' flex-row-reverse'
            }`}
          >
            <hr className="hidden w-full border-secondary md:block" />
            <button className="flex md:mx-2" onClick={toggleChildComments}>
              <p className="text-sm text-tertiary md:block md:whitespace-nowrap">
                <span className=" text-sm text-action-primary">
                  {childComments.length} repl{childComments.length === 1 ? 'y' : 'ies'},
                </span>{' '}
                <span className="hidden md:inline-block">Last reply at </span> {lastUpdateTime}
              </p>
            </button>
            {!showChildComments ? (
              <ChevronDownIcon
                className="hidden cursor-pointer md:block md:w-8"
                onClick={toggleChildComments}
                width={16}
              />
            ) : (
              <ChevronUpIcon
                className="hidden cursor-pointer md:block md:w-8"
                onClick={toggleChildComments}
                width={16}
              />
            )}
          </div>

          <div
            className={clsx(
              'grid transition-all duration-200 ease-in-out w-full',
              !showChildComments && 'grid-rows-[0fr] opacity-0',
              showChildComments && 'grid-rows-[1fr] opacity-100',
            )}
          >
            <div className={clsx('overflow-hidden w-full flex', rightAllignment && 'justify-end')}>
              <div className="mb-4 flex w-11/12 flex-col md:w-4/5">
                {childComments.map((childComment) => {
                  return (
                    <Comment
                      activeCommentToReply={activeCommentToReply}
                      data={childComment}
                      isReadOnly={isReadOnly}
                      key={childComment.id}
                      rightAllignment={rightAllignment}
                      scrolledComment={scrolledCurrentElement}
                      setActiveCommentToReply={setActiveCommentToReply}
                      supportedReactions={supportedReactions}
                      type="Child"
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
